import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";
import { confirm, DataTable, type TanstackReactTable } from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import { EditIcon, TrashIcon } from "lucide-react";
import type { CatalogProductListItem } from "../api/client";

const STATUS_LABELS: Record<string, string> = {
  draft: "ຮ່າງ",
  active: "ໃຊ້ງານ",
  hidden: "ເຊື່ອງ",
  archived: "ເກັບມ້ຽນ",
};
const TYPE_LABELS: Record<string, string> = {
  part_new: "ອາໄຫຼແທ້",
  part_oem: "ອາໄຫຼທຽບ (OEM)",
  part_used: "ອາໄຫຼຖອດແທ້",
  service: "ບໍລິການ",
};

type CatalogProductsTableProps = {
  canManage: boolean;
  isLoading: boolean;
  data: CatalogProductListItem[];
  offset: number;
  limit: number;
  totalCount: number;
  onEdit: (row: CatalogProductListItem) => void;
  onDelete: (id: string) => Promise<void>;
  onPaginationChange: (offset: number, limit: number) => void;
};

export function CatalogProductsTable({
  canManage,
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  onEdit,
  onDelete,
  onPaginationChange,
}: CatalogProductsTableProps) {
  const columns: TanstackReactTable.ColumnDef<CatalogProductListItem>[] = [
    {
      id: "order",
      header: "ລຳດັບ",
      cell: ({ row }) => offset + row.index + 1,
      size: 70,
    },
    {
      id: "name",
      header: "ຊື່ສິນຄ້າ",
      cell: ({ row }) => (
        <Link
          to="/app/catalog/$id"
          params={{ id: row.original.id }}
          className="text-primary hover:underline"
        >
          {row.original.name}
        </Link>
      ),
      size: 220,
    },
    {
      id: "type",
      header: "ປະເພດ",
      cell: ({ row }) => TYPE_LABELS[row.original.type] ?? row.original.type,
      size: 120,
    },
    {
      id: "category",
      header: "ຫມວດ",
      cell: ({ row }) => row.original.categoryName ?? "—",
      size: 140,
    },
    {
      id: "brand",
      header: "ແບຣນດ໌",
      cell: ({ row }) => row.original.brandName ?? "—",
      size: 120,
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) =>
        STATUS_LABELS[row.original.status] ?? row.original.status,
      size: 100,
    },
    {
      id: "updatedAt",
      header: "ວັນທີ່ອັບເດດ",
      cell: ({ row }) => formatDateTimeLocal(row.original.updatedAt),
      size: 120,
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        if (!canManage) return null;
        const id = row.original.id;
        const actions = [
          {
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="size-4" />,
            onClick: () => onEdit(row.original),
          },
          {
            label: "ລຶບ",
            variant: "destructive" as const,
            icon: <TrashIcon className="size-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ລຶບສິນຄ້າ",
                description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບສິນຄ້ານີ້? ຖ້າມີຮ້ານໃຊ້ຢູ່ຈະລຶບບໍ່ໄດ້.",
                actionText: "ລຶບ",
                ActionProps: { variant: "destructive" },
              });
              if (ok) await onDelete(id);
            },
          },
        ];
        return <RowActions actions={actions} maxInline={2} />;
      },
      size: 100,
    },
  ];

  return (
    <DataTable<CatalogProductListItem, unknown>
      noDataMessage="ບໍ່ພົບສິນຄ້າ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      enableSorting={false}
      sortBy="order"
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
    />
  );
}
